// src/components/stock/StockRankTable.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MarketSeriesChart, { IndicatorState, MarketView } from './MarketSeriesChart';

export type StockRow = {
  ticker: string;
  corp_name: string;
  market?: string;
  close?: number;
  volume?: number;
  price_change_percentage?: number;
  prev_close?: number;
  percentile?: number;
  rankChange?: number;

  tradingValue?: number;
  trading_value?: number;
  trading_value_day?: number;
  tradingValueShare?: number;
  tradingValueRank?: number;
  liquidityTier?: 'A' | 'B' | 'C' | string;

  rsLocalPct?: number;
  signalStrength?: number;
  hasSpikeLike?: boolean;
  priceStage?: string;
  flowStage?: string;
  crowdingStage?: string;
  actionLabel?: string;
  eventStage?: string;
  timingState?: string;
  foreignZ?: number;
  maGap20Pct?: number;
  maGap60Pct?: number;

  priorityScore?: number;
  contradictionScore?: number;
  confidenceScore?: number;
  decisionLabel?: string;
  trendLabel?: string;
  flowLabel?: string;

  policyCount7d?: number;
  directCount7d?: number;
  indirectCount7d?: number;
  maxArticleImpactScore?: number;
  avgArticleConfidence?: number;
  maxPolicyImpactScore?: number;
  hasDirectPolicy?: boolean;
  hasIndirectPolicy?: boolean;

  reportSector?: string;
  dailyIssueCount?: number;
  insightTitle?: string;
  insightSummary?: string;
  insightInterpretation?: string;
  insightRisk?: string;
  insightAction?: string;

  financialScore?: number;
  financial_score?: number;
  financialGrade?: string;
  financial_grade?: string;
  financialRiskLevel?: string;
  financial_risk_level?: string;
  financialOutlookLabel?: string;
  financialRiskLabel?: string;
  financialSummary?: string;

  pbr?: number;
  per?: number;
  bps?: number;
  eps?: number;
};

type ExtraIndicatorFlags = {
  vfi?: boolean;
  mfi?: boolean;
  volSMA5?: boolean;
  volSMA20?: boolean;
  volSpike?: boolean;
  buySellRatio?: boolean;
  lci?: boolean;
};

type Props = {
  rows: StockRow[];
  from?: string;
  to?: string;
  view: MarketView;
  indicators: IndicatorState & ExtraIndicatorFlags;
  onIndicatorsChange?: (indicators: IndicatorState & ExtraIndicatorFlags) => void;
  expandedTicker?: string | null;
  onExpandedTickerChange?: (ticker: string | null) => void;
  activeDate?: string | null;
  onActiveDateChange?: (date?: string | null) => void;
  selectedTicker?: string | null;
  onOpenFeedDetail?: (feedLike: any) => void;
  chartDefaultRange?: import('./MarketSeriesChart').ChartRangePreset;
};

type Tone = 'green' | 'blue' | 'amber' | 'red' | 'slate';
type DecisionBucket = 'Action' | 'Review' | 'Wait' | 'Risk' | 'Low';

const UI = {
  card: '#ffffff',
  cardSoft: '#f8fafc',
  border: '#dbe3ec',
  borderStrong: '#cbd5e1',
  text: '#111827',
  textSub: '#5f6b7a',
  textMuted: '#8a95a5',
  blue: '#0f3b5f',
  blueSoft: '#eaf2f8',
  green: '#2e7d32',
  greenSoft: '#ddf6df',
  amber: '#a36a00',
  amberSoft: '#fff1cc',
  red: '#c62828',
  redSoft: '#ffe1df',
  slate: '#64748b',
  slateSoft: '#eef2f7',
};

function useIsMobile(breakpoint = 900) {
  const getValue = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= breakpoint;
  }, [breakpoint]);

  const [isMobile, setIsMobile] = useState(getValue);

  useEffect(() => {
    const onResize = () => setIsMobile(getValue());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, [getValue]);

  return isMobile;
}

function safeNumber(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function firstText(...values: any[]) {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text && text !== 'null' && text !== 'undefined' && text !== '-') return text;
  }
  return '';
}

function formatNumber(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  return new Intl.NumberFormat('ko-KR').format(Math.round(Number(value)));
}

function formatPct(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';
  const n = Number(value);
  return `${n > 0 ? '+' : ''}${n.toFixed(2)}%`;
}

function compactMoney(value?: number | null) {
  if (value == null || !Number.isFinite(Number(value))) return '-';

  const v = Number(value);
  const abs = Math.abs(v);

  if (abs >= 1_0000_0000_0000) return `${(v / 1_0000_0000_0000).toFixed(1)}조`;
  if (abs >= 1_0000_0000) return `${(v / 1_0000_0000).toFixed(1)}억`;
  if (abs >= 1_0000) return `${(v / 1_0000).toFixed(1)}만`;

  return formatNumber(v);
}

function toneColor(tone: Tone) {
  if (tone === 'green') return UI.green;
  if (tone === 'blue') return UI.blue;
  if (tone === 'amber') return UI.amber;
  if (tone === 'red') return UI.red;
  return UI.slate;
}

function toneBg(tone: Tone) {
  if (tone === 'green') return UI.greenSoft;
  if (tone === 'blue') return UI.blueSoft;
  if (tone === 'amber') return UI.amberSoft;
  if (tone === 'red') return UI.redSoft;
  return UI.slateSoft;
}

function normalizeTicker(value?: string | null) {
  return String(value ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

function tradingValueOf(row: StockRow) {
  return safeNumber(row.tradingValue ?? row.trading_value ?? row.trading_value_day, 0);
}

function financialScoreOf(row?: StockRow) {
  if (!row) return undefined;

  const score = row.financialScore ?? row.financial_score;
  return Number.isFinite(Number(score)) ? Number(score) : undefined;
}

function financialRiskOf(row?: StockRow) {
  if (!row) return '';

  const score = financialScoreOf(row);

  return firstText(
    row.financialRiskLevel,
    row.financial_risk_level,
    row.financialRiskLabel,
    score == null ? '' : score >= 75 ? '낮음' : score >= 55 ? '보통' : '높음',
  );
}

function financialGradeOf(row?: StockRow) {
  if (!row) return '';

  const score = financialScoreOf(row);

  return firstText(
    row.financialGrade,
    row.financial_grade,
    row.financialOutlookLabel,
    score == null ? '' : score >= 75 ? '재무 양호' : score >= 55 ? '재무 보통' : '재무 주의',
  );
}

function trendLabel(row: StockRow) {
  if (row.trendLabel) return row.trendLabel;

  const priceStage = String(row.priceStage ?? '').toUpperCase();
  const actionLabel = String(row.actionLabel ?? '').toUpperCase();
  const pct = row.price_change_percentage ?? 0;

  if (['BREAKOUT', 'EARLY', 'INITIAL_EXPANSION', 'EXPANSION', 'UPTREND'].includes(priceStage)) {
    return '상승 추세';
  }

  if (
    ['PULLBACK', 'PULLBACK_OR_BASE', 'WAIT_PULLBACK', 'SCALE_IN_ON_PULLBACK'].includes(priceStage) ||
    actionLabel === 'WAIT_PULLBACK'
  ) {
    return '조정 확인';
  }

  if (['TAKE_PROFIT', 'REDUCE', 'AVOID_CHASE', 'EXTENDED'].includes(actionLabel) || priceStage === 'EXTENDED') {
    return '과열 주의';
  }

  if (actionLabel === 'OBSERVE' || actionLabel === 'WATCH' || priceStage === 'PRE_BREAKOUT') return '관찰';
  if (row.signalStrength != null && row.signalStrength > 0.7 && pct >= 0) return '상승 추세';
  if (row.signalStrength != null && row.signalStrength < -0.2) return '약세';

  return '신호 혼조';
}

function flowLabel(row: StockRow) {
  if (row.flowLabel) return row.flowLabel;

  const flowStage = String(row.flowStage ?? '').toUpperCase();

  if (['BROAD_ACCUMULATION', 'DUAL_ACCUM'].includes(flowStage)) return '동반 유입';
  if (['INSTITUTION_LEAD', 'INST_LEAD'].includes(flowStage)) return '기관 유입';
  if (flowStage === 'FOREIGN_LEAD') return '외인 유입';
  if (flowStage === 'DISTRIBUTION') return '매도 우세';

  if (row.foreignZ != null) {
    if (row.foreignZ >= 0.8) return '외인 강세';
    if (row.foreignZ <= -0.8) return '외인 약세';
  }

  return '수급 혼조';
}

function financialLabel(row: StockRow) {
  const score = financialScoreOf(row);
  const grade = financialGradeOf(row);
  const risk = financialRiskOf(row);

  if (score == null) return '미확인';
  if (risk.includes('높') || grade.includes('주의') || score < 50) return '취약';
  if (score >= 70 && !risk.includes('높')) return '양호';
  return '보통';
}

function inferDecisionBucket(row: StockRow): DecisionBucket {
  const decision = String(row.decisionLabel ?? '');
  const action = String(row.actionLabel ?? '').toUpperCase();
  const trend = trendLabel(row);
  const contradiction = row.contradictionScore ?? 0;
  const priority = row.priorityScore ?? 0;
  const confidence = row.confidenceScore ?? 0;
  const gap20 = row.maGap20Pct ?? 0;
  const financialRisk = financialRiskOf(row);
  const changePct = row.price_change_percentage ?? 0;

  if (/제외|회피|보류/.test(decision)) return 'Low';
  if (['TAKE_PROFIT', 'REDUCE', 'AVOID_CHASE', 'EXTENDED'].includes(action)) return 'Risk';
  if (contradiction >= 65 || gap20 >= 14 || financialRisk.includes('높')) return 'Risk';
  if (changePct <= -6 && (financialScoreOf(row) ?? 70) < 65) return 'Risk';
  if (/눌림|조정|과열/.test(decision) || trend === '과열 주의' || trend === '조정 확인') return 'Wait';
  if (priority >= 82 && confidence >= 58 && contradiction <= 35) return 'Action';
  if (priority >= 66 || row.hasDirectPolicy || (row.policyCount7d ?? 0) >= 2) return 'Review';

  return 'Review';
}

function decisionTone(bucket: DecisionBucket): Tone {
  if (bucket === 'Action') return 'blue';
  if (bucket === 'Review') return 'green';
  if (bucket === 'Wait') return 'amber';
  if (bucket === 'Risk') return 'red';
  return 'slate';
}

function policyScore(row: StockRow) {
  return Math.round(
    Math.min(
      100,
      Math.max(
        row.maxPolicyImpactScore ?? 0,
        row.maxArticleImpactScore ?? 0,
        row.hasDirectPolicy ? 74 : 0,
        (row.policyCount7d ?? 0) * 14,
        (row.directCount7d ?? 0) * 22,
        (row.indirectCount7d ?? 0) * 10,
      ),
    ),
  );
}

function marketScore(row: StockRow) {
  const priority = row.priorityScore ?? 0;
  const signal = (row.signalStrength ?? 0) * 100;
  const rs = row.rsLocalPct ?? 0;

  const tradingValue = tradingValueOf(row);
  const volume = row.volume ?? 0;
  const changePct = row.price_change_percentage ?? 0;

  let liquidityScore = 0;

  if (tradingValue >= 1_0000_0000_0000) liquidityScore = 90;
  else if (tradingValue >= 3000_0000_0000) liquidityScore = 78;
  else if (tradingValue >= 1000_0000_0000) liquidityScore = 66;
  else if (tradingValue >= 300_0000_0000) liquidityScore = 54;
  else if (tradingValue >= 100_0000_0000) liquidityScore = 42;
  else if (tradingValue > 0) liquidityScore = 30;

  let volumeScore = 0;

  if (volume >= 10_000_000) volumeScore = 75;
  else if (volume >= 3_000_000) volumeScore = 65;
  else if (volume >= 1_000_000) volumeScore = 55;
  else if (volume >= 300_000) volumeScore = 42;
  else if (volume > 0) volumeScore = 28;

  const priceReactionScore =
    Math.abs(changePct) >= 10 ? 70 :
    Math.abs(changePct) >= 5 ? 58 :
    Math.abs(changePct) >= 3 ? 46 :
    Math.abs(changePct) >= 1 ? 34 :
    20;

  const fallbackScore = Math.round(
    liquidityScore * 0.45 +
      volumeScore * 0.25 +
      priceReactionScore * 0.3,
  );

  return Math.round(
    Math.min(
      100,
      Math.max(priority, signal, rs, fallbackScore),
    ),
  );
}

function riskScore(row: StockRow) {
  const contradiction = row.contradictionScore ?? 0;
  const maGapRisk = (row.maGap20Pct ?? 0) > 12 ? 72 : 0;

  const financialScore = financialScoreOf(row);
  const financialRiskLabel = financialRiskOf(row);

  const financialRisk =
    financialScore == null ? 20 :
    financialRiskLabel.includes('높') ? 72 :
    financialScore < 45 ? 68 :
    financialScore < 55 ? 52 :
    financialScore < 70 ? 35 :
    20;

  const changePct = row.price_change_percentage ?? 0;

  const priceDropRisk =
    changePct <= -12 ? 80 :
    changePct <= -8 ? 68 :
    changePct <= -5 ? 55 :
    changePct <= -3 ? 42 :
    changePct < 0 ? 28 :
    15;

  const tradingValue = tradingValueOf(row);

  const largeMoveRisk =
    tradingValue >= 1_0000_0000_0000 && changePct <= -5 ? 62 :
    tradingValue >= 3000_0000_0000 && changePct <= -5 ? 55 :
    0;

  return Math.round(
    Math.min(
      100,
      Math.max(
        contradiction,
        maGapRisk,
        financialRisk,
        priceDropRisk,
        largeMoveRisk,
      ),
    ),
  );
}

function finalScore(row: StockRow) {
  const p = policyScore(row);
  const m = marketScore(row);

  const flow =
    flowLabel(row).includes('동반') ? 88 :
    flowLabel(row).includes('외인') ? 74 :
    flowLabel(row).includes('기관') ? 70 :
    flowLabel(row).includes('약세') || flowLabel(row).includes('매도') ? 35 :
    52;

  const confidence = safeNumber(row.confidenceScore ?? row.avgArticleConfidence ?? 50, 50);
  const risk = riskScore(row);

  return Math.round(
    p * 0.23 +
      m * 0.3 +
      flow * 0.2 +
      confidence * 0.14 +
      Math.max(0, 100 - risk) * 0.13,
  );
}

function oneLineReason(row: StockRow) {
  const bucket = inferDecisionBucket(row);
  const trend = trendLabel(row);
  const flow = flowLabel(row);

  if (row.insightInterpretation) return row.insightInterpretation;
  if (row.insightSummary) return row.insightSummary;

  if (bucket === 'Action') return `${trend}와 ${flow}이 동시에 확인됩니다. 거래대금 지속성을 먼저 확인하세요.`;
  if (bucket === 'Review') return `정책·수급 연결 가능성이 있습니다. 후속 이슈와 가격 위치를 함께 봐야 합니다.`;
  if (bucket === 'Wait') return `${trend} 상태입니다. 추격보다 눌림과 지지 확인이 우선입니다.`;
  if (bucket === 'Risk') return `가격·수급·재무 중 경계 신호가 있어 리스크 확인이 먼저입니다.`;
  return '현재 우선순위는 낮습니다. 재부각 또는 수급 전환 시 다시 확인하세요.';
}

function coreReasons(row: StockRow) {
  const reasons: string[] = [];

  if (row.hasDirectPolicy) reasons.push('직접 정책 연결');
  if ((row.directCount7d ?? 0) > 0) reasons.push(`직접 이슈 ${row.directCount7d}건`);
  if ((row.policyCount7d ?? 0) >= 2) reasons.push(`정책 이슈 ${row.policyCount7d}건`);
  if ((row.rsLocalPct ?? 0) >= 70) reasons.push('상대강도 상위');
  if ((row.signalStrength ?? 0) >= 0.8) reasons.push('관찰 강도 우위');
  if (row.liquidityTier === 'A') reasons.push('유동성 상');
  if (row.hasSpikeLike) reasons.push('거래 집중');
  if (flowLabel(row).includes('동반')) reasons.push('동반 수급');

  if (reasons.length === 0) reasons.push('가격·수급 데이터 기반 선별');

  return reasons.slice(0, 4);
}

function riskReasons(row: StockRow) {
  const risks: string[] = [];

  if ((row.contradictionScore ?? 0) >= 55) risks.push('가격·수급 모순');
  if ((row.maGap20Pct ?? 0) > 12) risks.push('20일선 이격');
  if ((row.foreignZ ?? 0) <= -0.8) risks.push('외인 약세');
  if ((financialScoreOf(row) ?? 100) < 50) risks.push('재무 취약');
  if ((row.price_change_percentage ?? 0) <= -5) risks.push('급락 반응');
  if (inferDecisionBucket(row) === 'Risk') risks.push('추격주의');

  if (risks.length === 0) risks.push('수급 지속성 확인');

  return risks.slice(0, 3);
}

function decisionLabelKo(bucket: DecisionBucket) {
  if (bucket === 'Action') return '우선 검토';
  if (bucket === 'Review') return '조건부 검토';
  if (bucket === 'Wait') return '관찰';
  return '제외';
}

function StatusBadge({ bucket }: { bucket: DecisionBucket }) {
  const tone = decisionTone(bucket);

  return (
    <span
      style={{
        ...styles.badge,
        color: toneColor(tone),
        background: toneBg(tone),
        borderColor: `${toneColor(tone)}55`,
      }}
    >
      {decisionLabelKo(bucket)}
    </span>
  );
}

function SmallBadge({
  children,
  tone = 'slate',
}: {
  children: React.ReactNode;
  tone?: Tone;
}) {
  return (
    <span
      style={{
        ...styles.smallBadge,
        color: toneColor(tone),
        background: toneBg(tone),
        borderColor: `${toneColor(tone)}44`,
      }}
    >
      {children}
    </span>
  );
}

function ScoreBar({
  label,
  value,
  tone = 'blue',
}: {
  label: string;
  value: number;
  tone?: Tone;
}) {
  const safeValue = Math.max(0, Math.min(100, Number(value) || 0));

  return (
    <div style={styles.scoreBar}>
      <div style={styles.scoreBarTop}>
        <span>{label}</span>
        <strong style={{ color: toneColor(tone) }}>{Math.round(safeValue)}</strong>
      </div>
      <div style={styles.scoreTrack}>
        <div
          style={{
            ...styles.scoreFill,
            width: `${safeValue}%`,
            background: toneColor(tone),
          }}
        />
      </div>
    </div>
  );
}

function InfoStat({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div style={styles.infoStat}>
      <span style={styles.infoLabel}>{label}</span>
      <strong style={styles.infoValue}>{value}</strong>
    </div>
  );
}

function ReasonList({
  items,
  tone,
}: {
  items: string[];
  tone: 'green' | 'amber';
}) {
  const color = tone === 'green' ? UI.green : UI.amber;

  return (
    <ul style={styles.reasonList}>
      {items.map((item) => (
        <li key={item} style={styles.reasonListItem}>
          <span style={{ ...styles.reasonListDot, background: color }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StockCard({
  row,
  selected,
  expanded,
  onToggleExpand,
  view,
  indicators,
  onIndicatorsChange,
  chartDefaultRange,
  activeDate,
  onActiveDateChange,
  rowRef,
}: {
  row: StockRow;
  selected: boolean;
  expanded: boolean;
  onToggleExpand: () => void;
  view: MarketView;
  indicators: IndicatorState & ExtraIndicatorFlags;
  onIndicatorsChange?: (indicators: IndicatorState & ExtraIndicatorFlags) => void;
  chartDefaultRange?: import('./MarketSeriesChart').ChartRangePreset;
  activeDate?: string | null;
  onActiveDateChange?: (date?: string | null) => void;
  rowRef: (el: HTMLDivElement | null) => void;
}) {
  const bucket = inferDecisionBucket(row);
  const tone = decisionTone(bucket);
  const priceTone: Tone = (row.price_change_percentage ?? 0) >= 0 ? 'green' : 'red';
  const policy = policyScore(row);
  const market = marketScore(row);
  const risk = riskScore(row);

  return (
    <article
      ref={rowRef}
      style={{
        ...styles.stockCard,
        borderColor: selected || expanded ? `${toneColor(tone)}88` : UI.border,
        boxShadow: selected || expanded ? `0 0 0 1px ${toneColor(tone)}22, 0 8px 18px rgba(15,23,42,.08)` : styles.stockCard.boxShadow,
      }}
    >
      <button type="button" style={styles.cardMainButton} onClick={onToggleExpand}>
        <div style={styles.cardHeader}>
          <div style={styles.cardTitleBox}>
            <div style={styles.stockNameLine}>
              <strong>{row.corp_name || '-'}</strong>
              <span>{row.ticker}</span>
            </div>

            <div style={styles.badgeRow}>
              <StatusBadge bucket={bucket} />
              <SmallBadge>{row.market || '-'}</SmallBadge>
              <SmallBadge tone={row.liquidityTier === 'A' ? 'green' : row.liquidityTier === 'B' ? 'blue' : 'slate'}>
                유동성 {row.liquidityTier || '-'}
              </SmallBadge>
              <SmallBadge tone={row.hasDirectPolicy ? 'green' : (row.policyCount7d ?? 0) > 0 ? 'blue' : 'slate'}>
                정책 {row.hasDirectPolicy ? '직접' : (row.policyCount7d ?? 0) > 0 ? `${row.policyCount7d}건` : '미확인'}
              </SmallBadge>
            </div>
          </div>

          <div style={styles.priceBox}>
            <div style={styles.price}>{formatNumber(row.close)}</div>
            <div style={{ ...styles.change, color: toneColor(priceTone) }}>{formatPct(row.price_change_percentage)}</div>
          </div>
        </div>

        <p style={styles.reasonText}>{oneLineReason(row)}</p>

        <div style={styles.infoGrid}>
          <InfoStat label="거래대금" value={compactMoney(tradingValueOf(row))} />
          <InfoStat label="수급" value={flowLabel(row)} />
          <InfoStat label="추세" value={trendLabel(row)} />
          <InfoStat label="재무" value={financialLabel(row)} />
        </div>

        <div style={styles.scoreGrid}>
          <ScoreBar label="정책" value={policy} tone={policy >= 70 ? 'green' : policy > 0 ? 'amber' : 'slate'} />
          <ScoreBar label="시장" value={market} tone={market >= 70 ? 'green' : market >= 45 ? 'blue' : 'slate'} />
          <ScoreBar label="위험" value={risk} tone={risk >= 70 ? 'red' : risk >= 45 ? 'amber' : 'green'} />
        </div>
      </button>

      <div style={styles.cardActions}>
        <button type="button" style={styles.primaryButton} onClick={onToggleExpand}>
          {expanded ? '차트 닫기' : '차트로 검토'}
        </button>
      </div>

      {expanded && (
        <div style={styles.expandedArea}>
          <div style={styles.expandedSummary}>
            <div style={styles.reasonCompactBlock}>
              <div style={styles.sectionLabel}>선정 근거</div>
              <ReasonList items={coreReasons(row)} tone="green" />
            </div>

            <div style={styles.reasonCompactBlock}>
              <div style={styles.sectionLabel}>확인할 위험</div>
              <ReasonList items={riskReasons(row)} tone="amber" />
            </div>
          </div>

          <div style={styles.chartBox}>
            <MarketSeriesChart
              ticker={row.ticker}
              days={undefined}
              view={view}
              indicators={indicators}
              onIndicatorsChange={(next) => onIndicatorsChange?.({ ...indicators, ...next })}
              title={`${row.corp_name} (${row.ticker}) — 가격·수급`}
              height={460}
              defaultRange={chartDefaultRange}
              showToolbar
              showRelatedIssues={false}
              showDecisionMarkers
              showEventMarkers
              decisionLabel={decisionLabelKo(bucket)}
              decisionSummary={oneLineReason(row)}
              activeDate={activeDate ?? null}
              onActiveDateChange={(date) => onActiveDateChange?.(date ?? null)}
            />
          </div>
        </div>
      )}
    </article>
  );
}


function DesktopStockTable({
  rows,
  expandedTicker,
  selectedTicker,
  onToggleExpand,
  view,
  indicators,
  onIndicatorsChange,
  chartDefaultRange,
  getActiveDate,
  onActiveDateChange,
  compareTickers,
  onToggleCompare,
}: {
  rows: StockRow[];
  expandedTicker: string | null;
  selectedTicker?: string | null;
  onToggleExpand: (ticker: string) => void;
  view: MarketView;
  indicators: IndicatorState & ExtraIndicatorFlags;
  onIndicatorsChange?: (indicators: IndicatorState & ExtraIndicatorFlags) => void;
  chartDefaultRange: import('./MarketSeriesChart').ChartRangePreset;
  getActiveDate: (ticker: string) => string | null;
  onActiveDateChange: (ticker: string, date: string | null) => void;
  compareTickers: string[];
  onToggleCompare: (ticker: string) => void;
}) {
  return (
    <div style={styles.desktopTableShell}>
      <div style={styles.tableScroll}>
        <table style={styles.desktopTable}>
          <thead>
            <tr>
              <th style={{ ...styles.thCenter, width: 40 }}>비교</th>
              <th style={{ ...styles.th, ...styles.stickyStockColumn }}>종목</th>
              <th style={styles.thRight}>현재가</th>
              <th style={styles.thRight}>등락</th>
              <th style={styles.thRight}>거래대금</th>
              <th style={styles.thCenter}>판단</th>
              <th style={styles.th}>핵심 신호</th>
              <th style={styles.thCenter}>차트</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const ticker = row.ticker;
              const expanded = expandedTicker === ticker;
              const selected = selectedTicker === ticker;
              const bucket = inferDecisionBucket(row);
              const bucketTone = decisionTone(bucket);
              const score = finalScore(row);
              const pScore = policyScore(row);
              const rScore = riskScore(row);
              const percentileRank = Math.max(
                1,
                Math.round(
                  100 - ((rows.filter((candidate) => finalScore(candidate) <= score).length - 1) / Math.max(1, rows.length)) * 100,
                ),
              );
              const priceTone =
                (row.price_change_percentage ?? 0) > 0
                  ? UI.red
                  : (row.price_change_percentage ?? 0) < 0
                    ? UI.blue
                    : UI.textSub;

              return (
                <React.Fragment key={ticker}>
                  <tr
                    onClick={() => onToggleExpand(ticker)}
                    style={{
                      ...styles.dataRow,
                      background: selected ? '#eef6ff' : expanded ? '#f7fbff' : '#fff',
                      boxShadow: selected ? 'inset 3px 0 0 #0f3b5f' : 'none',
                    }}
                  >
                    <td style={styles.tdCenter}>
                      <input
                        type="checkbox"
                        aria-label={`${row.corp_name} 비교 선택`}
                        checked={compareTickers.includes(ticker)}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => onToggleCompare(ticker)}
                        style={{ width: 15, height: 15, cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ ...styles.td, ...styles.stickyStockColumn }}>
                      <div style={styles.stockCell}>
                        <span style={styles.rankNumber}>{index + 1}</span>
                        <div style={{ minWidth: 0 }}>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              onToggleExpand(ticker);
                            }}
                            style={styles.stockNameButton}
                          >
                            {row.corp_name}
                          </button>
                          <div style={styles.stockMeta}>
                            {ticker}{row.market ? ` · ${row.market}` : ''}{row.reportSector ? ` · ${row.reportSector}` : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...styles.tdRight, fontWeight: 900 }}>{formatNumber(row.close)}</td>
                    <td style={{ ...styles.tdRight, color: priceTone, fontWeight: 950 }}>{formatPct(row.price_change_percentage)}</td>
                    <td style={styles.tdRight}>{compactMoney(tradingValueOf(row))}</td>
                    <td style={styles.tdCenter}>
                      <div style={{ display: 'grid', justifyItems: 'center', gap: 3 }}>
                        <span style={{ ...styles.tableSignal, color: toneColor(bucketTone), background: toneBg(bucketTone) }}>{bucket}</span>
                        <span style={{ color: UI.textMuted, fontSize: 9.5, fontWeight: 800 }}>종합 {score} · 상위 {percentileRank}%</span>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.signalStack}>
                        <span style={styles.signalPrimary}>{trendLabel(row)}</span>
                        <span style={styles.signalDivider}>·</span>
                        <span style={styles.signalPrimary}>{flowLabel(row)}</span>
                        <span style={styles.signalDivider}>·</span>
                        <span style={{ ...styles.signalPrimary, color: financialLabel(row) === '취약' ? UI.red : UI.textSub }}>{financialLabel(row)}</span>
                      </div>
                      <div style={styles.signalMeta}>
                        정책 {pScore} · 위험 <span style={{ color: rScore >= 65 ? UI.red : rScore >= 45 ? UI.amber : UI.green, fontWeight: 900 }}>{rScore}</span>
                      </div>
                    </td>
                    <td style={styles.tdCenter}>
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          onToggleExpand(ticker);
                        }}
                        style={{ ...styles.chartOpenButton, background: expanded ? UI.blue : '#fff', color: expanded ? '#fff' : UI.blue }}
                      >
                        {expanded ? '닫기' : '보기'}
                      </button>
                    </td>
                  </tr>

                  {expanded ? (
                    <tr>
                      <td colSpan={8} style={styles.expandedCell}>
                        <div style={styles.expandedHeader}>
                          <div style={{ minWidth: 0 }}>
                            <div style={styles.expandedTitle}>{row.corp_name} <span style={styles.expandedTicker}>{ticker}</span></div>
                            <div style={styles.expandedGuide}>{oneLineReason(row)}</div>
                          </div>
                          <div style={styles.expandedQuickFacts}>
                            <span>종합 {score}</span><span>정책 {pScore}</span><span>위험 {rScore}</span>
                          </div>
                        </div>

                        <MarketSeriesChart
                          ticker={ticker}
                          view={view}
                          indicators={indicators}
                          onIndicatorsChange={(next) => onIndicatorsChange?.({ ...indicators, ...next })}
                          title={`${row.corp_name} (${ticker})`}
                          height={560}
                          defaultRange={chartDefaultRange}
                          showToolbar
                          showRelatedIssues={false}
                          activeDate={getActiveDate(ticker)}
                          onActiveDateChange={(date) => onActiveDateChange(ticker, date ?? null)}
                        />

                        <div style={styles.reasonGrid}>
                          <div style={styles.reasonBox}>
                            <div style={styles.reasonTitle}>선정 근거</div>
                            <div style={styles.reasonText}>{oneLineReason(row)}</div>
                            <ReasonList items={coreReasons(row)} tone="green" />
                          </div>
                          <div style={styles.reasonBox}>
                            <div style={styles.reasonTitle}>확인할 위험</div>
                            <div style={styles.reasonText}>수급과 가격이 엇갈리거나 이격이 커질수록 추격보다 재확인이 우선입니다.</div>
                            <ReasonList items={riskReasons(row)} tone="amber" />
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}


type CompareSeriesRow = {
  ticker: string;
  name: string;
  color: string;
  points: Array<{ date: string; value: number }>;
  performance: number;
};

function StockComparisonPanel({
  rows,
  from,
  to,
  onClose,
}: {
  rows: StockRow[];
  from?: string;
  to?: string;
  onClose: () => void;
}) {
  const [series, setSeries] = useState<CompareSeriesRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const palette = useMemo(() => ['#0f3b5f', '#c62828', '#2e7d32', '#a36a00'], []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (rows.length < 2) return;
      setLoading(true);
      setError('');
      try {
        const loaded = await Promise.all(
          rows.map(async (row, index) => {
            const params = new URLSearchParams();
            if (from) params.set('from', from);
            if (to) params.set('to', to);
            const response = await fetch(
              `/api/category/stocks/${encodeURIComponent(row.ticker)}/daily?${params.toString()}`,
              { credentials: 'include', headers: { Accept: 'application/json' } },
            );
            if (!response.ok) throw new Error(`${row.corp_name} 데이터 조회 실패`);
            const payload = await response.json();
            const rawRows = Array.isArray(payload)
              ? payload
              : Array.isArray(payload?.items)
                ? payload.items
                : Array.isArray(payload?.data)
                  ? payload.data
                  : Array.isArray(payload?.rows)
                    ? payload.rows
                    : [];
            const normalized = rawRows
              .map((item: any) => ({
                date: String(item?.date ?? item?.tradeDate ?? item?.trade_date ?? '').slice(0, 10),
                close: Number(item?.close ?? item?.closePrice ?? item?.close_price),
              }))
              .filter((item: any) => item.date && Number.isFinite(item.close) && item.close > 0)
              .sort((a: any, b: any) => a.date.localeCompare(b.date));
            const first = normalized[0]?.close;
            const points = first
              ? normalized.map((item: any) => ({ date: item.date, value: ((item.close / first) - 1) * 100 }))
              : [];
            return {
              ticker: row.ticker,
              name: row.corp_name,
              color: palette[index % palette.length],
              points,
              performance: points.length ? points[points.length - 1].value : 0,
            };
          }),
        );
        if (!cancelled) setSeries(loaded);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || '비교 차트를 불러오지 못했습니다.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [from, rows, to, palette]);

  const chart = useMemo(() => {
    const allValues = series.flatMap((item) => item.points.map((point) => point.value));
    const allDates = Array.from(new Set(series.flatMap((item) => item.points.map((point) => point.date)))).sort();
    const min = allValues.length ? Math.min(...allValues, 0) : -5;
    const max = allValues.length ? Math.max(...allValues, 0) : 5;
    const span = Math.max(1, max - min);
    const width = 1000;
    const height = 330;
    const pad = { left: 58, right: 24, top: 22, bottom: 38 };
    const x = (date: string) => pad.left + (allDates.indexOf(date) / Math.max(1, allDates.length - 1)) * (width - pad.left - pad.right);
    const y = (value: number) => pad.top + ((max - value) / span) * (height - pad.top - pad.bottom);
    return { allDates, min, max, width, height, pad, x, y };
  }, [series]);

  return (
    <div style={{ marginBottom: 12, border: `1px solid ${UI.borderStrong}`, borderRadius: 13, background: '#fff', overflow: 'hidden', boxShadow: '0 16px 38px rgba(15,23,42,.09)' }}>
      <div style={{ padding: '13px 15px', background: '#071827', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 950 }}>선택 종목 수익률 비교</div>
          <div style={{ marginTop: 3, color: 'rgba(255,255,255,.5)', fontSize: 10.8 }}>모든 종목을 조회 시작일 0%로 정규화해 같은 기준으로 비교합니다.</div>
        </div>
        <button type="button" onClick={onClose} style={{ border: '1px solid rgba(255,255,255,.2)', background: 'transparent', color: '#fff', borderRadius: 8, padding: '7px 10px', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>비교 닫기</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 220px', gap: 0 }}>
        <div style={{ minWidth: 0, padding: 12, borderRight: `1px solid ${UI.border}` }}>
          {loading ? <div style={{ height: 330, display: 'grid', placeItems: 'center', color: UI.textSub, fontSize: 12 }}>비교 데이터를 불러오는 중입니다.</div> : null}
          {error ? <div style={{ height: 330, display: 'grid', placeItems: 'center', color: UI.red, fontSize: 12 }}>{error}</div> : null}
          {!loading && !error ? (
            <svg viewBox={`0 0 ${chart.width} ${chart.height}`} style={{ display: 'block', width: '100%', height: 330 }} role="img" aria-label="선택 종목 정규화 수익률 비교 차트">
              {[0, .25, .5, .75, 1].map((ratio) => {
                const value = chart.max - (chart.max - chart.min) * ratio;
                const y = chart.pad.top + (chart.height - chart.pad.top - chart.pad.bottom) * ratio;
                return <g key={ratio}><line x1={chart.pad.left} x2={chart.width-chart.pad.right} y1={y} y2={y} stroke="#e5ebf1" strokeWidth="1"/><text x={chart.pad.left-8} y={y+4} textAnchor="end" fill="#8a95a5" fontSize="11">{value.toFixed(1)}%</text></g>;
              })}
              <line x1={chart.pad.left} x2={chart.width-chart.pad.right} y1={chart.y(0)} y2={chart.y(0)} stroke="#98a2b3" strokeDasharray="5 5" />
              {series.map((item) => {
                const d = item.points.map((point, index) => `${index ? 'L' : 'M'} ${chart.x(point.date).toFixed(2)} ${chart.y(point.value).toFixed(2)}`).join(' ');
                return <path key={item.ticker} d={d} fill="none" stroke={item.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />;
              })}
              {chart.allDates.length ? <><text x={chart.pad.left} y={chart.height-12} fill="#8a95a5" fontSize="11">{chart.allDates[0]}</text><text x={chart.width-chart.pad.right} y={chart.height-12} fill="#8a95a5" fontSize="11" textAnchor="end">{chart.allDates[chart.allDates.length-1]}</text></> : null}
            </svg>
          ) : null}
        </div>
        <div style={{ padding: 12, background: '#f8fafc' }}>
          <div style={{ color: UI.textMuted, fontSize: 10.5, fontWeight: 900, marginBottom: 8 }}>기간 성과 순위</div>
          {[...series].sort((a,b) => b.performance-a.performance).map((item,index) => (
            <div key={item.ticker} style={{ padding: '9px 0', borderBottom: index === series.length-1 ? 'none' : `1px solid ${UI.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}><span style={{ width: 9, height: 9, borderRadius: '50%', background: item.color }}/><strong style={{ color: UI.text, fontSize: 12 }}>{item.name}</strong></div>
              <div style={{ marginTop: 4, paddingLeft: 16, display: 'flex', justifyContent: 'space-between', gap: 6 }}><span style={{ color: UI.textMuted, fontSize: 10.5 }}>{item.ticker}</span><strong style={{ color: item.performance >= 0 ? UI.red : UI.blue, fontSize: 12 }}>{item.performance >= 0 ? '+' : ''}{item.performance.toFixed(2)}%</strong></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const StockRankTable: React.FC<Props> = ({
  rows,
  from,
  to,
  view,
  indicators,
  onIndicatorsChange,
  chartDefaultRange = '3M',
  expandedTicker = null,
  onExpandedTickerChange,
  activeDate,
  onActiveDateChange,
  selectedTicker,
}) => {
  const viewportMobile = useIsMobile();
  const wrapRef = useRef<HTMLElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const isMobile = viewportMobile || (containerWidth > 0 && containerWidth < 760);
  const [compareTickers, setCompareTickers] = useState<string[]>([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const toggleCompare = useCallback((ticker: string) => {
    setCompareTickers((current) => {
      if (current.includes(ticker)) {
        const next = current.filter((item) => item !== ticker);
        if (next.length < 2) setCompareOpen(false);
        return next;
      }
      if (current.length >= 4) return [...current.slice(1), ticker];
      return [...current, ticker];
    });
  }, []);
  const [activeDatesByTicker, setActiveDatesByTicker] = useState<Record<string, string | null>>({});
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const safeRows = useMemo(() => rows || [], [rows]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    let frame = 0;
    const update = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const next = Math.floor(el.getBoundingClientRect().width);
        setContainerWidth((current) => (Math.abs(current - next) >= 2 ? next : current));
      });
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!expandedTicker || safeRows.length === 0) return;
    if (!safeRows.some((row) => normalizeTicker(row.ticker) === normalizeTicker(expandedTicker))) return;

    requestAnimationFrame(() => {
      rowRefs.current[expandedTicker]?.scrollIntoView?.({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }, [expandedTicker, safeRows]);

  const tableSummary = useMemo(() => {
    const total = safeRows.length;
    const action = safeRows.filter((row) => inferDecisionBucket(row) === 'Action').length;
    const review = safeRows.filter((row) => inferDecisionBucket(row) === 'Review').length;
    const observe = safeRows.filter((row) => inferDecisionBucket(row) === 'Wait').length;
    const exclude = safeRows.filter((row) => ['Risk', 'Low'].includes(inferDecisionBucket(row))).length;

    return { total, action, review, observe, exclude };
  }, [safeRows]);

  const setTickerActiveDate = (ticker: string, date: string | null) => {
    setActiveDatesByTicker((prev) => ({
      ...prev,
      [ticker]: date,
    }));
    onActiveDateChange?.(date);
  };

  const getEffectiveActiveDate = (ticker: string) => {
    if (selectedTicker === ticker && activeDate != null) return activeDate;
    return activeDatesByTicker[ticker] ?? null;
  };

  const handleToggleExpand = (ticker: string) => {
    const next = expandedTicker === ticker ? null : ticker;

    if (!next) {
      onActiveDateChange?.(null);
    }

    onExpandedTickerChange?.(next);
  };

  if (safeRows.length === 0) {
    return (
      <div style={styles.emptyCard}>
        <div style={styles.emptyTitle}>표시할 종목이 없습니다.</div>
        <div style={styles.emptyText}>검색어 또는 필터 조건을 조정해서 다시 확인하세요.</div>
      </div>
    );
  }

  return (
    <section ref={wrapRef} style={{ ...styles.wrap, minWidth: 0, width: '100%' }}>
      {compareTickers.length > 0 ? (
        <div style={{
          position: 'sticky',
          bottom: 12,
          zIndex: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          background: '#fff',
          color: UI.text,
          border: `1px solid ${UI.border}`,
          borderRadius: 10,
          padding: '10px 12px',
          marginBottom: 10,
          boxShadow: '0 1px 4px rgba(15,23,42,.04)',
        }}>
          <div>
            <strong style={{ fontSize: 12.5 }}>비교 종목 {compareTickers.length}/4</strong>
            <div style={{ color: 'rgba(255,255,255,.55)', fontSize: 10.5, marginTop: 2 }}>
              {safeRows.filter((row) => compareTickers.includes(row.ticker)).map((row) => row.corp_name).join(' · ')}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 7 }}>
            <button type="button" disabled={compareTickers.length < 2} onClick={() => setCompareOpen(true)} style={{ border: `1px solid ${compareTickers.length >= 2 ? UI.blue : UI.border}`, background: compareTickers.length >= 2 ? UI.blue : UI.cardSoft, color: compareTickers.length >= 2 ? '#fff' : UI.textMuted, borderRadius: 8, padding: '7px 11px', fontSize: 11, fontWeight: 950, cursor: compareTickers.length >= 2 ? 'pointer' : 'not-allowed' }}>
              차트 비교
            </button>
            <button type="button" onClick={() => { setCompareTickers([]); setCompareOpen(false); }} style={{ border: `1px solid ${UI.border}`, background: '#fff', color: UI.textSub, borderRadius: 8, padding: '7px 10px', fontSize: 11, fontWeight: 900, cursor: 'pointer' }}>
              선택 초기화
            </button>
          </div>
        </div>
      ) : null}

      {compareOpen && compareTickers.length >= 2 ? (
        <StockComparisonPanel
          rows={safeRows.filter((row) => compareTickers.includes(row.ticker))}
          from={from}
          to={to}
          onClose={() => setCompareOpen(false)}
        />
      ) : null}

      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 8,
        padding: '0 2px',
      }}>
        <div>
          <div style={{ color: UI.text, fontSize: 13.5, fontWeight: 950 }}>후보 비교</div>
          <div style={{ color: UI.textMuted, fontSize: 10.5, marginTop: 2 }}>
            필요한 종목만 펼쳐 세부 지표와 차트를 확인합니다.
          </div>
        </div>
        <div style={{ color: UI.textMuted, fontSize: 10.5, fontWeight: 850 }}>최대 4개 비교</div>
      </div>

      <div style={styles.summaryBar}>
        <SmallBadge tone="blue">표시 {tableSummary.total}개</SmallBadge>
        <SmallBadge tone="blue">우선 검토 {tableSummary.action}</SmallBadge>
        <SmallBadge tone="green">조건부 검토 {tableSummary.review}</SmallBadge>
        <SmallBadge tone="amber">관찰 {tableSummary.observe}</SmallBadge>
        <SmallBadge tone="red">제외 {tableSummary.exclude}</SmallBadge>
      </div>

      {isMobile ? (
        <div style={{ ...styles.cardGrid, gridTemplateColumns: '1fr' }}>
          {safeRows.map((row) => (
            <StockCard
              key={row.ticker}
              row={row}
              selected={selectedTicker === row.ticker}
              expanded={expandedTicker === row.ticker}
              onToggleExpand={() => handleToggleExpand(row.ticker)}
              view={view}
              indicators={indicators}
              onIndicatorsChange={onIndicatorsChange}
              chartDefaultRange={chartDefaultRange}
              activeDate={getEffectiveActiveDate(row.ticker)}
              onActiveDateChange={(date) => setTickerActiveDate(row.ticker, date ?? null)}
              rowRef={(el) => {
                rowRefs.current[row.ticker] = el;
              }}
            />
          ))}
        </div>
      ) : (
        <DesktopStockTable
          rows={safeRows}
          expandedTicker={expandedTicker}
          selectedTicker={selectedTicker}
          onToggleExpand={handleToggleExpand}
          view={view}
          indicators={indicators}
          onIndicatorsChange={onIndicatorsChange}
          chartDefaultRange={chartDefaultRange}
          getActiveDate={getEffectiveActiveDate}
          onActiveDateChange={setTickerActiveDate}
          compareTickers={compareTickers}
          onToggleCompare={toggleCompare}
        />
      )}
    </section>
  );
};

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    width: '100%',
    minWidth: 0,
  },
  summaryBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    background: 'transparent',
    border: 'none',
    borderRadius: 0,
    padding: '4px 2px 8px',
    marginBottom: 6,
    boxShadow: 'none',
  },
  cardGrid: {
    display: 'grid',
    gap: 12,
    alignItems: 'start',
  },
  stockCard: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 10,
    boxShadow: '0 1px 3px rgba(15,23,42,.05)',
    overflow: 'hidden',
    transition: '160ms ease',
  },
  cardMainButton: {
    display: 'block',
    width: '100%',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    padding: 12,
    cursor: 'pointer',
    color: UI.text,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 12,
  },
  cardTitleBox: {
    minWidth: 0,
  },
  stockNameLine: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 9,
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderRadius: 999,
    padding: '5px 10px',
    fontSize: 12,
    fontWeight: 950,
    whiteSpace: 'nowrap',
  },
  smallBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid',
    borderRadius: 999,
    padding: '4px 8px',
    fontSize: 11,
    fontWeight: 900,
    whiteSpace: 'nowrap',
  },
  priceBox: {
    textAlign: 'right',
    flex: '0 0 auto',
  },
  price: {
    fontSize: 16,
    fontWeight: 950,
    color: UI.text,
  },
  change: {
    marginTop: 4,
    fontSize: 13,
    fontWeight: 950,
  },
  reasonText: {
    margin: '0 0 14px',
    color: UI.textSub,
    fontSize: 14,
    lineHeight: 1.65,
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: 8,
    marginBottom: 14,
  },
  infoStat: {
    minWidth: 0,
  },
  infoLabel: {
    display: 'block',
    color: UI.textMuted,
    fontSize: 11,
    fontWeight: 850,
    marginBottom: 4,
  },
  infoValue: {
    display: 'block',
    color: UI.text,
    fontSize: 13,
    fontWeight: 950,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  scoreGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: 8,
  },
  scoreBar: {
    minWidth: 0,
  },
  scoreBarTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
    color: UI.textSub,
    fontSize: 12,
    fontWeight: 800,
    marginBottom: 5,
  },
  scoreTrack: {
    width: '100%',
    height: 7,
    borderRadius: 999,
    background: '#e7edf4',
    overflow: 'hidden',
  },
  scoreFill: {
    height: '100%',
    borderRadius: 999,
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '0 16px 16px',
  },
  primaryButton: {
    height: 36,
    borderRadius: 11,
    border: `1px solid ${UI.blue}`,
    background: UI.blue,
    color: '#fff',
    padding: '0 13px',
    fontSize: 13,
    fontWeight: 850,
    cursor: 'pointer',
  },
  secondaryButton: {
    height: 36,
    borderRadius: 11,
    border: `1px solid ${UI.borderStrong}`,
    background: UI.card,
    color: UI.text,
    padding: '0 13px',
    fontSize: 13,
    fontWeight: 850,
    cursor: 'pointer',
  },
  expandedArea: {
    borderTop: `1px solid ${UI.border}`,
    background: UI.cardSoft,
    padding: 14,
  },
  expandedSummary: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: 12,
    marginBottom: 12,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: 950,
    color: UI.textMuted,
    marginBottom: 8,
  },
  reasonChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
  },
  reasonCompactBlock: {
    minWidth: 0,
    padding: '8px 9px',
    border: `1px solid ${UI.border}`,
    borderRadius: 8,
    background: UI.cardSoft,
  },
  reasonList: {
    listStyle: 'none',
    padding: 0,
    margin: '6px 0 0',
    display: 'grid',
    gap: 5,
  },
  reasonListItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 6,
    color: UI.textSub,
    fontSize: 10,
    lineHeight: 1.45,
    fontWeight: 800,
  },
  reasonListDot: {
    width: 5,
    height: 5,
    marginTop: 5,
    borderRadius: 999,
    flex: '0 0 auto',
  },
  chartBox: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 10,
    padding: 12,
    overflow: 'hidden',
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 10,
  },
  textButton: {
    border: 'none',
    background: 'transparent',
    color: UI.blue,
    fontSize: 12,
    fontWeight: 850,
    cursor: 'pointer',
  },
  emptyCard: {
    background: UI.card,
    border: `1px solid ${UI.border}`,
    borderRadius: 10,
    padding: 28,
    textAlign: 'center',
    color: UI.textSub,
  },
  emptyTitle: {
    fontSize: 17,
    fontWeight: 950,
    color: UI.text,
  },
  emptyText: {
    marginTop: 8,
    fontSize: 14,
    color: UI.textSub,
  },
  desktopTableShell: {
    background: '#fff',
    border: `1px solid ${UI.borderStrong}`,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(15,23,42,.045)',
  },
  tableScroll: {
    width: '100%',
    overflowX: 'auto',
  },
  desktopTable: {
    width: '100%',
    minWidth: 960,
    borderCollapse: 'separate',
    borderSpacing: 0,
    fontSize: 12.5,
  },
  th: {
    position: 'sticky',
    top: 0,
    zIndex: 5,
    padding: '11px 10px',
    background: '#f7f9fc',
    color: UI.textMuted,
    borderBottom: `1px solid ${UI.borderStrong}`,
    textAlign: 'left',
    whiteSpace: 'nowrap',
    fontSize: 11.5,
    fontWeight: 950,
  },
  thRight: {
    position: 'sticky',
    top: 0,
    zIndex: 4,
    padding: '11px 10px',
    background: '#f7f9fc',
    color: UI.textMuted,
    borderBottom: `1px solid ${UI.borderStrong}`,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    fontSize: 11.5,
    fontWeight: 950,
  },
  thCenter: {
    position: 'sticky',
    top: 0,
    zIndex: 4,
    padding: '11px 10px',
    background: '#f7f9fc',
    color: UI.textMuted,
    borderBottom: `1px solid ${UI.borderStrong}`,
    textAlign: 'center',
    whiteSpace: 'nowrap',
    fontSize: 11.5,
    fontWeight: 950,
  },
  stickyStockColumn: {
    position: 'sticky',
    left: 0,
    zIndex: 6,
    minWidth: 220,
    boxShadow: '1px 0 0 #dbe3ec',
  },
  dataRow: {
    cursor: 'pointer',
    transition: 'background 120ms ease',
  },
  td: {
    padding: '10px 9px',
    borderBottom: `1px solid ${UI.border}`,
    background: 'inherit',
    color: UI.text,
    whiteSpace: 'nowrap',
  },
  tdRight: {
    padding: '10px 9px',
    borderBottom: `1px solid ${UI.border}`,
    textAlign: 'right',
    color: UI.text,
    whiteSpace: 'nowrap',
  },
  tdCenter: {
    padding: '10px 9px',
    borderBottom: `1px solid ${UI.border}`,
    textAlign: 'center',
    color: UI.textSub,
    whiteSpace: 'nowrap',
  },
  stockCell: {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    minWidth: 0,
  },
  rankNumber: {
    width: 24,
    color: UI.textMuted,
    fontSize: 11,
    fontWeight: 850,
    textAlign: 'right',
    flex: '0 0 auto',
  },
  stockNameButton: {
    display: 'block',
    border: 'none',
    padding: 0,
    background: 'transparent',
    color: UI.text,
    fontSize: 13,
    fontWeight: 950,
    cursor: 'pointer',
    textAlign: 'left',
  },
  stockMeta: {
    color: UI.textMuted,
    fontSize: 10.5,
    marginTop: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 195,
  },
  tableSignal: {
    display: 'inline-flex',
    minWidth: 58,
    justifyContent: 'center',
    padding: '4px 7px',
    borderRadius: 6,
    fontSize: 10.5,
    fontWeight: 950,
  },
  signalStack: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    minWidth: 0,
    whiteSpace: 'nowrap',
  },
  signalPrimary: {
    color: UI.textSub,
    fontSize: 10.5,
    fontWeight: 850,
  },
  signalDivider: {
    color: UI.borderStrong,
    fontSize: 10,
  },
  signalMeta: {
    marginTop: 3,
    color: UI.textMuted,
    fontSize: 9.3,
    fontWeight: 750,
  },
  chartOpenButton: {
    border: `1px solid ${UI.blue}`,
    borderRadius: 7,
    padding: '5px 9px',
    fontSize: 11,
    fontWeight: 900,
    cursor: 'pointer',
  },
  expandedCell: {
    padding: 0,
    borderBottom: `1px solid ${UI.borderStrong}`,
    background: '#f7f9fc',
    boxShadow: 'inset 0 10px 24px rgba(15,23,42,.035)',
  },
  expandedHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: '14px 16px 10px',
  },
  expandedTitle: {
    color: UI.text,
    fontSize: 16,
    fontWeight: 950,
  },
  expandedTicker: {
    color: UI.textMuted,
    fontSize: 12,
    fontWeight: 800,
  },
  expandedGuide: {
    color: UI.textSub,
    fontSize: 12,
    marginTop: 4,
  },
  expandedQuickFacts: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    color: UI.blue,
    fontSize: 11.5,
    fontWeight: 900,
  },
  reasonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2,minmax(0,1fr))',
    gap: 10,
    padding: '10px 14px 14px',
  },
  reasonBox: {
    background: '#fff',
    border: `1px solid ${UI.border}`,
    borderRadius: 9,
    padding: 12,
  },
  reasonTitle: {
    color: UI.text,
    fontSize: 12,
    fontWeight: 950,
  },
  goodReasonChip: {
    color: UI.green,
    background: UI.greenSoft,
    borderRadius: 5,
    padding: '4px 6px',
    fontSize: 10.5,
    fontWeight: 850,
  },
  riskReasonChip: {
    color: UI.red,
    background: UI.redSoft,
    borderRadius: 5,
    padding: '4px 6px',
    fontSize: 10.5,
    fontWeight: 850,
  },

};

export default StockRankTable;
